import type {
  SectionExerciseGroup,
  SectionExercise,
  LessonSection as WorkoutSection,
  Equipment,
  LessonSection,
} from 'app/types/graphql';

import { lengthFromExercise } from 'app/utils/duration-handler';

export type VideoData = {
  src: string;
  duration: string;
  name: string;
  thumbnail?: string;
  key?: string;
};

export type WorkoutType = {
  id: string;
  name: string;
  trainers: string;
  duration: string;
  concept: string;
  intensity: string;
  sections: WorkoutSection[];
  imageUrl: string;
  description: string;
  equipment: Equipment[];
  isCompleted: boolean;
  isSaved: boolean;
};

type GetVideoDataProps = {
  focusedContentItem: string | null;
  exercises: SectionExercise[];
  exercisePicked?: string | null;
};

export const findSectionById = (sections: WorkoutSection[], sectionId: string) =>
  sections.find((element: any) => element.id === sectionId);

export const flattenSectionsToExercises = (sections: WorkoutSection[]): SectionExercise[] => {
  return sections.reduce((accumulatedExercises: SectionExercise[], section: WorkoutSection) => {
    const sectionExercises: SectionExercise[] = (section.sectionExerciseGroups || []).reduce(
      (groupExercises: SectionExercise[], group: SectionExerciseGroup) => {
        const exercises = [...groupExercises, ...(group.sectionExercises || [])];
        if (group.sectionExerciseGroups) {
          const nestedExercises = group.sectionExerciseGroups.flatMap(
            (nestedGroup) => nestedGroup.sectionExercises || [],
          );
          exercises.push(...nestedExercises);
        }
        return exercises;
      },
      [],
    );
    return [...accumulatedExercises, ...sectionExercises];
  }, []);
};

type SectionExerciseWithinGroup = {
  sectionExercise: SectionExercise | null;
};

export const findSectionExerciseInSection = (
  section: WorkoutSection | null,
  sectionExerciseId?: string | null,
): SectionExercise | null => {
  if (!section || !section?.sectionExerciseGroups) {
    return null;
  }

  if (!sectionExerciseId) {
    return (
      section.sectionExerciseGroups?.[0]?.sectionExercises?.[0] ||
      section.sectionExerciseGroups?.[0]?.sectionExerciseGroups?.[0]?.sectionExercises?.[0] ||
      null
    );
  }

  const result = section.sectionExerciseGroups.reduce(
    (search: SectionExerciseWithinGroup | null, group: SectionExerciseGroup) => {
      if (search) {
        return search;
      }

      const sectionExercise = group?.sectionExercises?.find(
        (sectionExercise) => sectionExercise.id === sectionExerciseId,
      );

      if (sectionExercise) {
        return { sectionExercise };
      }

      if (group.sectionExerciseGroups) {
        const nestedExercise = group.sectionExerciseGroups
          .flatMap((nestedGroup) => nestedGroup.sectionExercises || [])
          .find((exercise) => exercise.id === sectionExerciseId);

        if (nestedExercise) {
          return { sectionExercise: nestedExercise };
        }
      }

      return null;
    },
    null,
  );

  return result?.sectionExercise || section.sectionExerciseGroups?.[0]?.sectionExercises?.[0] || null;
};

export const getVideoData = ({
  focusedContentItem,
  exercises,
  exercisePicked,
}: GetVideoDataProps): VideoData | null => {
  if (focusedContentItem) {
    if (exercises?.length > 0) {
      const focusedSectionExercise = exercises.find((exercise: SectionExercise) => exercise.id === focusedContentItem);

      if (focusedSectionExercise?.exercise && focusedSectionExercise.exercise.id !== exercisePicked) {
        const { alternativeExercises } = focusedSectionExercise.exercise;

        const alternativeExerciseToShow = alternativeExercises?.find(({ id }) => id === exercisePicked);

        if (alternativeExerciseToShow?.video) {
          const { video } = alternativeExerciseToShow;

          return {
            name: alternativeExerciseToShow.name,
            src: video.url || video.signedUrl,
            duration: lengthFromExercise(focusedSectionExercise),
            thumbnail: video?.thumbnail?.url || video?.thumbnail?.signedUrl,
            key: `${focusedSectionExercise.id}_video`,
          };
        }
      }

      if (focusedSectionExercise?.exercise?.video) {
        const video = focusedSectionExercise.exercise.video;
        return {
          name: focusedSectionExercise.exercise.name,
          src: video.url || video.signedUrl,
          duration: lengthFromExercise(focusedSectionExercise),
          thumbnail: video?.thumbnail?.url || video?.thumbnail?.signedUrl,
          key: `${focusedSectionExercise.id}_video`,
        };
      }
    }
  }

  return null;
};

export const indexToLetter = (index: number): string => String.fromCharCode(97 + index);

export type WorkoutVariant = 'regular' | 'pdf';

export const isPdf = (variant: WorkoutVariant = 'regular'): boolean => variant === 'pdf';

export const formatDuration = (seconds: number | null | undefined, forRemainingTimeText = 'FRT'): string => {
  if (!seconds || seconds <= 0) {
    return '';
  }
  if (seconds === -1) {
    return forRemainingTimeText;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const getLessonSectionDescription = (lessonSection: LessonSection) => {
  const { workTime, restTime, rounds, description } = lessonSection;

  if (description && description.trim().length > 0) {
    return description.trim();
  }

  const sectionRounds = rounds || 1;

  if (typeof workTime === 'number' && typeof restTime === 'number') {
    const formattedWorkTime = formatDuration(workTime);
    const formattedRestTime = formatDuration(restTime);
    return `Rep/Interval Scheme: ${formattedWorkTime} Work/${formattedRestTime} Rest, ${sectionRounds} ${sectionRounds === 1 ? 'Round' : 'Rounds'}`;
  }

  return null;
};
